import React, { forwardRef } from "react";
import { spacing } from "../design-tokens";
import { useDensity } from "./context";
import type { Density } from "./context";

/**
 * Stack / Row — layout con gap tokenizzato.
 *
 * Esiste per una ragione precisa: il check LYT-07 conta le spaziature larghe
 * (`gap-8`, `space-y-8`) sparse a mano. Finché mettere spazio a mano è più
 * comodo che usare un token, quel check continuerà a fallire. Stack rende il
 * token la strada breve.
 *
 * `gap` accetta una chiave della scala di spacing, non un numero: non è
 * possibile scrivere un valore arbitrario senza passare dall'escape hatch.
 */

export type SpacingKey = keyof typeof spacing;

export interface StackProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "className"> {
  as?: "div" | "section" | "ul" | "li" | "nav" | "header" | "footer";
  /** Direzione. `row` allinea verticalmente al centro. */
  direction?: "column" | "row";
  /** Chiave della scala di spacing. In assenza, deriva dalla densità. */
  gap?: SpacingKey;
  density?: Density;
  align?: React.CSSProperties["alignItems"];
  justify?: React.CSSProperties["justifyContent"];
  wrap?: boolean;
  className?: string;
  children?: React.ReactNode;
}

const DENSITY_GAP: Record<Density, SpacingKey> = {
  compact: "sm", // 8px
  comfortable: "md", // 12px
};

function cx(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

export const Stack = forwardRef<HTMLDivElement, StackProps>(function Stack(
  {
    as = "div",
    direction = "column",
    gap,
    density: densityProp,
    align,
    justify,
    wrap = false,
    className,
    style,
    children,
    ...rest
  },
  ref,
) {
  const density = useDensity(densityProp);
  const resolvedGap = spacing[gap ?? DENSITY_GAP[density]];

  const Component = as as React.ElementType;

  return (
    <Component
      {...rest}
      ref={ref}
      className={cx(
        "tl4k-stack",
        direction === "row" && "tl4k-stack--row",
        wrap && "tl4k-stack--wrap",
        className,
      )}
      style={
        {
          "--tl4k-stack-gap": resolvedGap,
          ...(align ? { alignItems: align } : null),
          ...(justify ? { justifyContent: justify } : null),
          ...style,
        } as React.CSSProperties
      }
    >
      {children}
    </Component>
  );
});

/** Scorciatoia per `<Stack direction="row">`. */
export const Row = forwardRef<HTMLDivElement, Omit<StackProps, "direction">>(function Row(
  props,
  ref,
) {
  return <Stack {...props} ref={ref} direction="row" />;
});
